import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, createContext } from 'react';
import { useQuery as useApolloQuery, useMutation as useApolloMutation, useLazyQuery as useLazyApolloQuery, ApolloLink, ApolloClient, InMemoryCache, fromPromise } from '@apollo/client';
const DataFetchingClientContext = /*#__PURE__*/createContext({});

function useDataFetchingClient() {
  const {
    client
  } = useContext(DataFetchingClientContext); // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore process is declared in webpack-env, but doesn't impact this module's type externally so we can ignore in downstream builds

  if (process.env.NODE_ENV !== 'production') {
    if (!client) {
      throw new Error('Cannot find context. Did you forget to configure `DataFetchingClientProvider`');
    }
  }

  return client;
}

function DataFetchingClientProvider({
  client,
  children
}) {
  return /*#__PURE__*/_jsx(DataFetchingClientContext.Provider, {
    value: {
      client
    },
    children: children
  });
} // See https://spec.graphql.org/June2018/#sec-Names


const isValidGraphQLName = str => typeof str === 'string' && /^[_A-Za-z][_0-9A-Za-z]+$/.test(str);

const registeredQueries = new WeakMap();

class DataFetchingClientLink extends ApolloLink {
  constructor({
    client
  }) {
    super();
    this.client = client;
  }

  request({
    variables,
    query
  }) {
    const fieldDef = registeredQueries.get(query);

    if (!fieldDef) {
      throw new Error('`data-fetching-client` only supports queries created with `registerQuery` or `registerMutation`.');
    }

    const {
      fetcher,
      fieldName
    } = fieldDef;
    return fromPromise(Promise.resolve(fetcher(variables, {
      client: this.client
    }))).map(result => ({
      data: {
        [fieldName]: result
      }
    }));
  }

}

class DataFetchingClient extends ApolloClient {
  constructor({
    queryFieldPolicies,
    assumeImmutableResults,
    defaultOptions
  } = {}) {
    super({
      defaultOptions,
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: Object.assign({}, queryFieldPolicies)
          }
        },
        addTypename: false
      }),
      assumeImmutableResults
    });
    this.setLink(new DataFetchingClientLink({
      client: this
    })); // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore process is declared in webpack-env, but doesn't impact this module's type externally so we can ignore in downstream builds

    if (process.env.NODE_ENV !== 'production') {
      window.__DATA_FETCHING_CLIENT__ = this;
    }
  }

}

function registerOperation(operation, {
  fieldName,
  args = [],
  fetcher,
  queryDef
}) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore process is declared in webpack-env, but doesn't impact this module's type externally so we can ignore in downstream builds
  if (process.env.NODE_ENV !== 'production') {
    if (!isValidGraphQLName(fieldName)) {
      throw new Error(`"${fieldName}" is not a valid GraphQL Name and cannot be used as \`fieldName\`. https://spec.graphql.org/June2018/#sec-Names`);
    }

    args.forEach(arg => {
      if (!isValidGraphQLName(arg)) {
        throw new Error(`"${String(arg)}" is not a valid GraphQL Name and cannot be used in \`args\`. https://spec.graphql.org/June2018/#sec-Names`);
      }
    });
  }

  const query = {
    kind: 'Document',
    definitions: [{
      kind: 'OperationDefinition',
      operation,
      name: {
        kind: 'Name',
        value: fieldName
      },
      variableDefinitions: args.map(arg => ({
        kind: 'VariableDefinition',
        variable: {
          kind: 'Variable',
          name: {
            kind: 'Name',
            value: arg
          }
        },
        type: {
          kind: 'NamedType',
          name: {
            kind: 'Name',
            value: 'JSON'
          }
        }
      })),
      selectionSet: {
        kind: 'SelectionSet',
        selections: [{
          kind: 'Field',
          name: {
            kind: 'Name',
            value: fieldName
          },
          directives: typeof fetcher === 'undefined' ? [{
            kind: 'Directive',
            name: {
              kind: 'Name',
              value: 'client'
            }
          }] : [],
          selectionSet: queryDef && queryDef.definitions[0].kind === 'OperationDefinition' ? queryDef.definitions[0].selectionSet : undefined,
          arguments: args.map(arg => ({
            kind: 'Argument',
            name: {
              kind: 'Name',
              value: arg
            },
            value: {
              kind: 'Variable',
              name: {
                kind: 'Name',
                value: arg
              }
            }
          }))
        }]
      }
    }]
  };
  registeredQueries.set(query, {
    fetcher,
    fieldName
  });
  return query;
}

function registerQuery(options) {
  return registerOperation('query', options);
}

function registerMutation(options) {
  return registerOperation('mutation', options);
}

function useQuery(query, options) {
  const {
    client
  } = useContext(DataFetchingClientContext);
  return useApolloQuery(query, Object.assign({
    client
  }, options));
}

function useLazyQuery(query, options) {
  const {
    client
  } = useContext(DataFetchingClientContext);
  return useLazyApolloQuery(query, Object.assign({
    client
  }, options));
}

function useMutation(query, options) {
  const {
    client
  } = useContext(DataFetchingClientContext);
  return useApolloMutation(query, Object.assign({
    client
  }, options));
}

export { DataFetchingClientProvider, DataFetchingClient, registerQuery, registerMutation, useDataFetchingClient, useQuery, useLazyQuery, useMutation };